import { getImageReference } from "@tiicker/util/lib/contentful/helpers";
import {
  ContentfulContentType,
  PerkDocument,
} from "@tiicker/util/lib/contentful/types";
import SwiperCarousel from "components/Carousel/SwiperCarousel";
import { useUser } from "components/UserContext/UserContext";
import { compact } from "lodash";
import React, { Fragment, useRef, useState } from "react";
import { useContentfulEntries } from "source/contentful/provider";
import PerkMegaModal from "../PerkMegaModal/PerkMegaModal";
import PopularPerkCard from "./PopularPerkCard";
import styles from "./PopularPerkSlider.module.scss";
import { isAfter } from "date-fns";

type Props = {
  perkIds: string[];
};

let endDate = new Date();
const today = new Date();
const PopularPerkSlider = (props: Props) => {
  const userContext = useUser();
  const [perk, setPerk] = useState<{ perkId: string; tickerSymbol: string }>({
    perkId: "",
    tickerSymbol: "",
  });

  const perks = useContentfulEntries<PerkDocument>(ContentfulContentType.Perk, {
    query: {
      content_type: ContentfulContentType.Perk,
      "sys.id[in]": props.perkIds.join(","),
    },
  });

  if (perks.state !== "DONE" || perks.result.items.length === 0) {
    return <></>;
  }

  const perksExcludingTiiCKER = compact(
    props.perkIds.map((p) => {
      const perk = perks.result.items.find(
        (contentfulPerk) => contentfulPerk.sys.id === p
      );

      if (perk?.fields.brand.fields.tickerSymbol.toLowerCase() === "tiicker") {
        return undefined;
      }

      const endDateString = perk?.fields.endDate; // Assuming endDate is a string

      if (endDateString) {
        const utcDate = new Date(endDateString);
        endDate = new Date(
          utcDate.getUTCFullYear(),
          utcDate.getUTCMonth(),
          utcDate.getUTCDate(),
          utcDate.getUTCHours(),
          utcDate.getUTCMinutes(),
          utcDate.getUTCSeconds()
        );
      }
      if (isAfter(today, endDate)) {
        return undefined; // Exclude items where today is after endDate
      }

      return perk;
    })
  );

  return (
    <div>
      <SwiperCarousel
        slides={perksExcludingTiiCKER
          .filter((perk) => new Date(perk.fields.endDate) > new Date())
          .slice(0, 4)
          .map((perk, i) => {
            const brandLogo = getImageReference(
              perk.fields.brand.fields.companyLogo,
              perks.result.includes
            );
            const perkImage = getImageReference(
              perk.fields.brand.fields.heroImage,
              perks.result.includes
            );

            return (
              <Fragment key={`featured-perk-${perk.sys.id}`}>
                {perk && (
                  <PopularPerkCard
                    title={perk.fields.title}
                    tickerSymbol={perk.fields.brand.fields.tickerSymbol}
                    imageUrl={perkImage?.fields.file.url ?? ""}
                    logoUrl={brandLogo?.fields.file.url ?? ""}
                    availableToClaim={userContext.state.availablePerks.some(
                      (availablePerk) => availablePerk.perkId === perk.sys.id
                    )}
                    onClick={() =>
                      setPerk({
                        perkId: perk.sys.id,
                        tickerSymbol: perk.fields.brand.fields.tickerSymbol,
                      })
                    }
                  />
                )}
              </Fragment>
            );
          })}
        spaceBetween={16}
        pagination
        navigation
        openSide="right"
        slideClass={styles.slide}
        dotLabels={perksExcludingTiiCKER
          .slice(0, 4)
          .map((x) => x.fields.brand.fields.brandName)}
        dotLabelClass={styles.dotLabel}
        bulletsClass={styles.bulletsClass}
      />
      {perk.perkId && perk.tickerSymbol && (
        <PerkMegaModal
          perkId={perk.perkId}
          isOpen={perk.perkId !== "" && perk.tickerSymbol !== ""}
          onClose={() => setPerk({ perkId: "", tickerSymbol: "" })}
          ticker={perk.tickerSymbol}
        />
      )}
    </div>
  );
};

export default PopularPerkSlider;
